// src/components/chat/OfferBubble.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { respondToOffer } from "../../utils/offerEngine";
import * as offersService from "../../services/offersService";
import OFFER_CONFIG from "../../config/offerConfig";
import { errorDev } from '../../utils/logger.js';

const offerCache = new Map();

function formatAmount(value) {
  if (value == null) return "—";
  const num = Number(value);
  if (Number.isFinite(num)) return `${num.toLocaleString()} RWF`;
  return `${value} RWF`;
}

function deriveStatusFromOfferState(offer, fallbackStatus = null) {
  const dealStatus = String(offer?.dealStatus || "").trim().toLowerCase();
  const baseStatus = String(offer?.status || fallbackStatus || OFFER_CONFIG.STATUS.PENDING).trim().toLowerCase();
  const sent = Boolean(offer?.deliverySentAt || offer?.chatDeal?.sentAt || offer?.chatDeal?.sent);
  const received = Boolean(offer?.deliveryReceivedAt || offer?.chatDeal?.receivedAt || offer?.chatDeal?.received);

  if (received || dealStatus.includes("complet") || dealStatus.includes("receiv")) return "completed";
  if (baseStatus.includes("declin") || baseStatus.includes("cancel") || baseStatus.includes("reject")) {
    return OFFER_CONFIG.STATUS.DECLINED;
  }
  if (sent || dealStatus.includes("sent") || dealStatus.includes("ship") || dealStatus.includes("transit")) {
    return OFFER_CONFIG.STATUS.ACCEPTED;
  }
  if (baseStatus.includes("accept") || dealStatus.includes("accept") || dealStatus.includes("approv")) {
    return OFFER_CONFIG.STATUS.ACCEPTED;
  }
  return OFFER_CONFIG.STATUS.PENDING;
}

export default function OfferBubble({
  msg,
  currentUser,
  isSender,
  align = "right",
  offerDoc, 
  status: propStatus, 
  onAccept, 
  onDecline,
  onCounterOffer,
}) {
  const { t } = useTranslation(["inbox", "common"]);
  const offerId =
    msg?.extra?.offerId ||
    msg?.offerId ||
    msg?.extra?.offerRefId ||
    msg?.offerRefId ||
    null;

  const [internalOffer, setInternalOffer] = useState(() =>
    offerId && offerCache.get(offerId) ? offerCache.get(offerId) : null
  );
  
  const offer = offerDoc || internalOffer;
  
  const [internalStatus, setInternalStatus] = useState(
    propStatus || msg?.extra?.offerStatus || msg?.offerStatus || offer?.status || OFFER_CONFIG.STATUS.PENDING
  );

  const status = useMemo(() => {
    const fallback = propStatus || internalStatus || msg?.extra?.offerStatus || msg?.offerStatus || OFFER_CONFIG.STATUS.PENDING;
    return deriveStatusFromOfferState(offer, fallback);
  }, [offer, propStatus, internalStatus, msg]);
  
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let ignore = false;
    if (!offerId || offerDoc) return;

    (async () => {
      try {
        if (offerCache.has(offerId)) {
          const cached = offerCache.get(offerId);
          if (!ignore && mounted.current) {
            setInternalOffer(cached);
            setInternalStatus(deriveStatusFromOfferState(cached, internalStatus));
          }
          return;
        }

        const fresh = await offersService.getOfferById(offerId);
        if (ignore || !mounted.current || !fresh) return;

        offerCache.set(offerId, fresh);
        setInternalOffer(fresh);
        setInternalStatus(deriveStatusFromOfferState(fresh, OFFER_CONFIG.STATUS.PENDING));
      } catch (err) {}
    })();

    return () => { ignore = true; };
  }, [offerId, offerDoc]);

  const isSeller = useMemo(() => {
    if (!offer || !currentUser) return false;
    return String(currentUser.uid) === String(offer.sellerId);
  }, [offer, currentUser]);

  const isBuyer = useMemo(() => {
    if (!offer || !currentUser) return false;
    return String(currentUser.uid) === String(offer.buyerId);
  }, [offer, currentUser]);

  const handleResponse = async (nextStatus) => {
    if (!offerId || busy) return;
    
    if (nextStatus === OFFER_CONFIG.STATUS.ACCEPTED && onAccept) {
      onAccept();
      return;
    }
    
    if (nextStatus === OFFER_CONFIG.STATUS.DECLINED && onDecline) {
      onDecline();
      return;
    }
    
    const prevStatus = status;
    setBusy(true);
    setError("");
    
    if (!propStatus) {
      setInternalStatus(nextStatus);
    }
    
    try {
      await respondToOffer(offerId, nextStatus, currentUser.uid);
      
      try { await offersService.updateOfferMessageStatus(offerId, nextStatus); } catch (err) { errorDev("Operation failed:", err); }
      
      try {
        const fresh = await offersService.getOfferById(offerId);
        if (fresh) {
          offerCache.set(offerId, fresh);
          if (!propStatus) setInternalOffer(fresh);
        }
      } catch (err) { errorDev("Operation failed:", err); }
    } catch (err) {
      if (mounted.current && !propStatus) {
        setInternalStatus(prevStatus);
        setError(`Failed to ${nextStatus === OFFER_CONFIG.STATUS.ACCEPTED ? 'accept' : 'decline'} offer. Please try again.`);
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const amount = msg?.extra?.offerAmount ?? msg?.offerAmount ?? offer?.amount;
  const counterToOfferId = msg?.extra?.counterToOfferId || offer?.metadata?.counterToOfferId || null;

  // Resolve original price for strikethrough on counter offers
  let originalPrice = msg?.extra?.originalPrice ?? null;
  if (!originalPrice && counterToOfferId) {
    const original = offerCache.get(counterToOfferId);
    if (original) {
      originalPrice = original.amount;
    }
  }
  const showActions = status === OFFER_CONFIG.STATUS.PENDING && isSeller && !isSender;
  const colorConfig = OFFER_CONFIG.STATUS_STYLE[status] || OFFER_CONFIG.STATUS_STYLE[OFFER_CONFIG.STATUS.PENDING];
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
  const directionLabel = isSender
    ? t("inbox:offerDirectionSent", "You sent this offer")
    : t("inbox:offerDirectionReceived", "You received this offer");

  const hasStrikethrough = originalPrice && Number(originalPrice) !== Number(amount);

  return (
    <div className={`flex ${isSender ? "justify-end" : "justify-start"} my-2 px-2 sm:px-0`}>
      {/* Light inline timeline block - no card, no border, no shadow */}
      <div
        className={`w-full max-w-[min(92vw,26rem)] sm:max-w-sm px-3 py-2.5 ${colorConfig.bg}`}
      >
        {/* Top row: amount (with strikethrough if counter) + status badge */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <span className="text-[10px] font-medium text-muted-foreground leading-tight">
              {t("inbox:offerAmount", "Offer")}
            </span>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className={`font-bold text-lg leading-none ${colorConfig.text}`}>
                {formatAmount(amount)}
              </span>
              {hasStrikethrough && (
                <del className="text-xs text-muted-foreground line-through">
                  {formatAmount(originalPrice)}
                </del>
              )}
            </div>
          </div>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${colorConfig.badge}`}>
            {statusLabel}
          </span>
        </div>

        {/* Direction + role chips (subtle, no bg boxes) */}
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-[10px] text-muted-foreground font-medium">{directionLabel}</span>
          {isSeller && (
            <span className="text-[10px] text-muted-foreground font-medium">{t("inbox:roleSeller", "Seller")}</span>
          )}
          {isBuyer && (
            <span className="text-[10px] text-muted-foreground font-medium">{t("inbox:roleBuyer", "Buyer")}</span>
          )}
        </div>

        {/* Optional message text */}
        {msg?.text && !String(msg.text).startsWith("Offer:") && (
          <p className={`text-sm leading-relaxed mb-2 ${colorConfig.text}`}>{msg.text}</p>
        )}

        {counterToOfferId && (
          <p className="text-[10px] text-muted-foreground mb-2">
            {t("inbox:counterTo", "Counter to offer #{{id}}", { id: String(counterToOfferId).slice(0, 8) })}
          </p>
        )}

        {/* Human conversational status text */}
        <p className="text-xs leading-relaxed mb-2.5 text-muted-foreground">
          {status === OFFER_CONFIG.STATUS.ACCEPTED && isSeller &&
            t("inbox:offerAcceptedSellerStep", "Offer accepted! Plan your pickup or payment details below.")}
          {status === OFFER_CONFIG.STATUS.ACCEPTED && isBuyer &&
            t("inbox:offerAcceptedBuyerStep", "Offer accepted! Confirm details and mark as received when the item arrives.")}
          {status === OFFER_CONFIG.STATUS.ACCEPTED && !isSeller && !isBuyer &&
            t("inbox:offerAcceptedCoordination", "Offer accepted. Plan the pickup and payment details.")}
          {status === "completed" &&
            t("inbox:offerCompleted", "Item received — deal complete.")}
          {status === OFFER_CONFIG.STATUS.DECLINED &&
            t("inbox:offerDeclinedRetry", "Offer declined. Try adjusting the price.")}
          {status === OFFER_CONFIG.STATUS.PENDING && isSeller &&
            t("inbox:offerSellerPrompt", "New offer. Accept, decline, or counter.")}
          {status === OFFER_CONFIG.STATUS.PENDING && isBuyer &&
            t("inbox:offerPendingSellerResponse", "Offer sent. Waiting for the seller to respond.")}
        </p>

        {/* Error inline */}
        {error && (
          <div className="mb-2 p-1.5 rounded bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20">
            <p className="text-xs text-red-700 dark:text-red-300 font-medium">{error}</p>
          </div>
        )}

        {/* Action buttons - inline, full-width pairs, no inner borders */}
        {showActions && (
          <div className="space-y-1.5">
            <button
              onClick={() => handleResponse(OFFER_CONFIG.STATUS.ACCEPTED)}
              disabled={busy}
              className={`w-full py-2.5 rounded-lg text-sm font-bold text-center transition-all ${OFFER_CONFIG.BUTTONS.accept} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {busy ? t("common:loading", "Loading...") : t("inbox:accepted", "Accept")}
            </button>

            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={() => handleResponse(OFFER_CONFIG.STATUS.DECLINED)}
                disabled={busy}
                className={`py-2.5 rounded-lg text-xs font-bold text-center transition-all ${OFFER_CONFIG.BUTTONS.decline} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {t("inbox:declined", "Decline")}
              </button>
              <button
                onClick={() => onCounterOffer && onCounterOffer()}
                disabled={busy || !onCounterOffer}
                className="py-2.5 rounded-lg text-xs font-bold text-center transition-all border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("inbox:counterOffer", "Counter Offer")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
